import React, { useRef, useState, useEffect, useMemo } from 'react'
import { Box, Tooltip as MuiTooltip, IconButton, Menu, MenuItem } from '@mui/material'
import { SciChartReact } from 'scichart-react'
import {
  SciChartSurface,
  NumericAxis,
  DateTimeNumericAxis,
  FastLineRenderableSeries,
  XyDataSeries,
  EAutoRange,
  EAxisAlignment,
  EExecuteOn,
  EXyDirection,
  MouseWheelZoomModifier,
  ZoomExtentsModifier,
  ZoomPanModifier,
  RubberBandXyZoomModifier,
  CursorModifier,
  NumberRange
} from 'scichart'
import { ChartTitle } from '../UI/ChartTitle'
import { LegendCompact, curvesToLegendItems } from '../UI/LegendCompact'
import { LegendStrip } from '../UI/LegendStrip'
import { ChartCloseButton } from '../Controls/ChartCloseButton'
import { DragOverlay } from '../UI/DragOverlay'
import { EmptyChartPlaceholder } from '../UI/EmptyChartPlaceholder'
import { ChartThemeManager } from '../Services/ChartThemeManager'
import { ChartSeriesFactory } from '../Services/ChartSeriesFactory'
import { TimeLabelProvider } from '../../../../lib/scichart/TimeLabelProvider'
import { useChartDropZone } from '../Hooks/useChartDragDrop'
import { ChartHeightPicker } from '../Controls/ChartHeightPicker'
import { ZoomControls } from '../Controls/ZoomControls'
import LegendBottomIcon from '../Controls/LegendBottomIcon'
import LegendNoneIcon from '../Controls/LegendNoneIcon'
import { CrosshairsControl } from '../Controls/CrosshairsControl'
import { TooltipsControl } from '../Controls/TooltipsControl'
import { useThemeContext } from '../../../../contexts/ThemeContext'
import type { WaveChart, ChartHeightPreset } from '../../../../interfaces/waveChart'
import type { ZoomMode } from '../Controls/ZoomControls'

export interface ChartContainerProps {
  /** Unique identifier for this chart */
  chartId: string
  
  /** Chart configuration and data */
  chart: WaveChart
  
  /** Initial height preset */
  heightPreset?: ChartHeightPreset
  
  /** Legend corner position */
  legendCorner?: 'tl' | 'tr' | 'bl' | 'br'
  /** Notify parent when legend corner changes */
  onLegendCornerChange?: (chartId: string, corner: 'tl' | 'tr' | 'bl' | 'br' | 'none') => void
  /** Notify parent when legend mode changes (overlay/bottom/none) */
  onLegendModeChange?: (chartId: string, mode: 'overlay' | 'bottom' | 'none') => void
  
  /** Whether this chart is currently being dragged over */
  isDraggedOver?: boolean
  
  /** Enable mouse wheel zoom on this chart */
  enableMouseWheelZoom?: boolean
  
  // Event handlers
  onClose?: (chartId: string) => void
  onHeightChange?: (chartId: string, height: number) => void
  onDragOverChange?: (chartId: string | null) => void
  onSurfaceReady?: (chartId: string, surface: any) => void
  onSurfaceDestroyed?: (chartId: string) => void
  /** When user clicks an item in the legend, provide a shortcut to open curve properties */
  onLegendItemClick?: (chartId: string, curveId: string) => void
  /** When user clicks the legend row, toggle curve visibility */
  onLegendToggleVisibility?: (chartId: string, curveId: string) => void
  
  // Style overrides
  style?: React.CSSProperties
  className?: string
}

/**
 * ChartContainer - Clean, simple chart component
 * 
 * Based on working TooltipDemo pattern, focused on getting basic functionality working.
 */
export const ChartContainer: React.FC<ChartContainerProps> = ({
  chartId,
  chart,
  heightPreset = 'medium',
  isDraggedOver = false,
  enableMouseWheelZoom = true,
  legendCorner = 'tr',
  onLegendCornerChange,
  onLegendModeChange,
  onClose,
  onHeightChange,
  onDragOverChange,
  onLegendItemClick,
  onSurfaceReady,
  style,
  className
}) => {
  // Get theme from context
  const { mode: theme } = useThemeContext()
  
  const chartRef = useRef<any>(null)
  const wasmContextRef = useRef<any>(null)
  
  // Control state management (internal defaults)
  const currentHeightPreset = heightPreset || 'medium' // Use prop directly
  const [currentZoomMode, setCurrentZoomMode] = useState<ZoomMode>('sliceX') // Default to horizontal slice
  const [actualHeight, setActualHeight] = useState<number>(() => {
    const presetHeights = { small: 250, medium: 400, large: 550, xl: 700 }
    return presetHeights[currentHeightPreset]
  })
  const [crosshairsEnabled, setCrosshairsEnabled] = useState<boolean>(false) // Default to disabled
  const [tooltipsEnabled, setTooltipsEnabled] = useState<boolean>(false) // Default to disabled
  const [currentLegendCorner, setCurrentLegendCorner] = useState<'tl' | 'tr' | 'bl' | 'br'>(legendCorner || 'tr')
  const [currentLegendMode, setCurrentLegendMode] = useState<'overlay' | 'bottom' | 'none'>(chart.legendMode ?? 'overlay')
  const [legendMenuAnchor, setLegendMenuAnchor] = useState<null | HTMLElement>(null)
  // Ephemeral hidden-state for legend toggling (not persisted)
  const [hiddenCurveIds, setHiddenCurveIds] = useState<Set<string>>(() => new Set())
  // Track if we already performed an initial auto-zoom
  const didInitialAutoZoom = useRef<boolean>(false)

  useEffect(() => {
    setCurrentLegendCorner(legendCorner)
  }, [legendCorner])

  useEffect(() => {
    setCurrentLegendMode(chart.legendMode ?? 'overlay')
  }, [chart.legendMode])

  const handleLegendCornerToggle = (_e: any, value: 'tl' | 'tr' | 'bl' | 'br' | null) => {
    if (!value) return
    setCurrentLegendCorner(value)
    onLegendCornerChange?.(chartId, value)
  }

  const openLegendMenu = (e: React.MouseEvent<HTMLElement>) => setLegendMenuAnchor(e.currentTarget)
  const closeLegendMenu = () => setLegendMenuAnchor(null)
  const selectLegendCorner = (value: 'tl' | 'tr' | 'bl' | 'br') => {
    // Optimistically switch to overlay and update corner
    setCurrentLegendMode('overlay')
    handleLegendCornerToggle(null, value)
    onLegendCornerChange?.(chartId, value)
    onLegendModeChange?.(chartId, 'overlay')
    closeLegendMenu()
  }

  const CornerIcon: React.FC<{ corner: 'tl'|'tr'|'bl'|'br' }> = ({ corner }) => {
    const square = (active: boolean) => ({
      width: 6, height: 6, borderRadius: 2,
      backgroundColor: active ? colors.text.primary : colors.text.secondary,
      opacity: active ? 1 : 0.5
    })
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 6px)', gridTemplateRows: 'repeat(2, 6px)', gap: '2px', padding: '2px' }}>
        <Box sx={square(corner==='tl')} />
        <Box sx={square(corner==='tr')} />
        <Box sx={square(corner==='bl')} />
        <Box sx={square(corner==='br')} />
      </Box>
    )
  }
  
  // Update actual height when heightPreset prop changes (for rendering)
  useEffect(() => {
    const presetHeights = { small: 250, medium: 400, large: 550, xl: 700 }
    const newHeight = presetHeights[currentHeightPreset]
    setActualHeight(newHeight)
  }, [currentHeightPreset])

  // Apply zoom mode when chart is ready
  useEffect(() => {
    if (chartRef.current) {
      updateZoomModifiers(currentZoomMode)
    }
  }, [currentZoomMode])

  // Update cursor modifiers when crosshairs or tooltips state changes
  useEffect(() => {
    if (chartRef.current) {
      updateCursorModifiers()
    }
  }, [crosshairsEnabled, tooltipsEnabled, theme])

  // Update axis titles and ranges when chart configuration changes
  useEffect(() => {
    const updateAxes = async () => {
      if (chartRef.current && chart) {
        console.log(`🔍 CHART_CHANGE_DEBUG: Axis update effect triggered for chart ${chartId}`)
        console.log(`🔍 CHART_CHANGE_DEBUG: Chart object reference:`, chart)
        console.log(`🔍 CHART_CHANGE_DEBUG: Chart curves count:`, chart.curves?.length)
        console.log(`🔍 CHART_CHANGE_DEBUG: Chart yAxes config:`, chart.yAxes)
        
        // Use NumberRange from static import instead of dynamic import
        
        // Update Y-axis titles and ranges based on current chart configuration
        chartRef.current.yAxes.asArray().forEach((yAxis: any) => {
          // Match by exact axis ID
          const axisId = yAxis.id || 'left'
          const axisConfig = chart.yAxes?.[axisId as keyof typeof chart.yAxes]
          
          // Construct title with subtle unit styling in parentheses
          let newTitle = 'Y Axis'
          if (axisConfig?.label && axisConfig?.unit) {
            newTitle = `${axisConfig.label} (${axisConfig.unit})`
          } else if (axisConfig?.label) {
            newTitle = axisConfig.label
          } else if (axisConfig?.unit) {
            newTitle = axisConfig.unit
          } else {
            // Fallback to curve unit if no explicit config
            newTitle = getCurveUnitForAxis(chart.curves, axisId) || 'Y Axis'
          }
          
          // Update title if changed
          if (yAxis.axisTitle !== newTitle) {
            console.log(`Updating axis ${axisId} title from "${yAxis.axisTitle}" to "${newTitle}"`)
            yAxis.axisTitle = newTitle
          }
          
          // Update range configuration if changed
          const hasManualRange = !!axisConfig?.range
          const currentAutoRange = yAxis.autoRange
          const expectedAutoRange = hasManualRange ? EAutoRange.Never : EAutoRange.Once
          
          console.log(`🔍 AXIS_UPDATE_DEBUG: Y-axis ${axisId} - current: ${currentAutoRange}, expected: ${expectedAutoRange}, hasManualRange: ${hasManualRange}`)
          
          if (currentAutoRange !== expectedAutoRange) {
            console.log(`🔍 AXIS_UPDATE_DEBUG: Updating axis ${axisId} autoRange from ${currentAutoRange} to ${expectedAutoRange}`)
            yAxis.autoRange = expectedAutoRange
            
            // If switching back to auto range, trigger zoom to fit
            if (!hasManualRange && currentAutoRange === EAutoRange.Never) {
              console.log(`🚨 SUSPICIOUS: Axis update effect wants to call zoomExtents() for axis ${axisId} - this might interfere with zoom operations!`)
              console.log(`🔍 AXIS_UPDATE_DEBUG: Triggering zoom to fit for axis ${axisId} after switching to auto range`)
              // Use setTimeout to ensure the autoRange change is applied first
              setTimeout(() => {
                console.log(`🚨 SUSPICIOUS: Actually calling zoomExtents() for axis ${axisId}`)
                chartRef.current?.zoomExtents()
              }, 10)
            }
                      } else {
            console.log(`🔍 AXIS_UPDATE_DEBUG: No change needed for axis ${axisId}`)
          }
          
          // Set manual range if configured
          if (hasManualRange && axisConfig.range) {
            const newRange = new NumberRange(axisConfig.range.min, axisConfig.range.max)
            
            // Only update if different (avoid unnecessary updates)
            const currentRange = yAxis.visibleRange
            if (!currentRange || currentRange.min !== newRange.min || currentRange.max !== newRange.max) {
              console.log(`🎯 Updating axis ${axisId} range to: ${newRange.min} - ${newRange.max}`)
              yAxis.visibleRange = newRange
            }
          }
        })
        
        // Force SciChart to re-render the chart surface if any changes were made
        chartRef.current?.invalidateElement()
      }
    }
    
    updateAxes()
  }, [chart, chartId])

  // Helper to compute curveId from WaveCurve (matches LegendCompact)
  const getCurveIdFromCurve = (c: any): string | undefined => {
    const fileType = c?.sourceFile
    if (!fileType || typeof c?.name !== 'string') return undefined
    const prefix = `${fileType}: `
    const curveNameForId = c.name.startsWith(prefix) ? c.name.substring(prefix.length) : c.name
    return `${fileType}:${curveNameForId}`
  }

  // Build legend items reflecting hidden state so dimming works
  const legendItems = useMemo(() => {
    const items = curvesToLegendItems(chart.curves || [])
    return items.map(item => {
      if (!item.curveId) return item
      const isHidden = hiddenCurveIds.has(item.curveId)
      return { ...item, visible: !isHidden }
    })
  }, [chart.curves, hiddenCurveIds])

  // Local legend toggle: toggle hidden set; do not persist
  const handleLegendToggleLocal = (item: { curveId?: string }) => {
    if (!item.curveId) return
    setHiddenCurveIds(prev => {
      const next = new Set(prev)
      if (next.has(item.curveId!)) next.delete(item.curveId!)
      else next.add(item.curveId!)
      return next
    })
  }

  // Update curves when chart data or hidden state changes
  useEffect(() => {
    const updateCurves = async () => {
      if (chartRef.current && wasmContextRef.current && chart && chart.curves) {
      console.log(`📊 ChartContainer updating curves for chart ${chartId} (${chart.curves.length} curves)`)
      
      // 1. Update Y-axes based on current curve requirements
      const currentAxisIds = new Set<string>()
      chart.curves.forEach(curve => {
        const yAxisId = curve.yAxisId || 'left'
        currentAxisIds.add(yAxisId)
      })
      
      // Get existing axes
      const existingAxisIds = new Set<string>()
      const yAxisMap = new Map<string, any>()
      chartRef.current.yAxes.asArray().forEach((axis: any) => {
        // Determine axis ID from alignment and position
        let axisId = 'left'
        if (axis.axisAlignment === EAxisAlignment.Right) {
          axisId = axis.id.includes('2') ? 'right2' : 'right'
        } else {
          axisId = axis.id.includes('2') ? 'left2' : 'left'
        }
        existingAxisIds.add(axisId)
        yAxisMap.set(axisId, axis)
      })
      
      // Remove unused axes first
      existingAxisIds.forEach(axisId => {
        if (!currentAxisIds.has(axisId)) {
          console.log(`🗑️ Removing unused Y-axis: ${axisId}`)
          const axisToRemove = yAxisMap.get(axisId)
          if (axisToRemove) {
            chartRef.current.yAxes.remove(axisToRemove)
          }
        }
      })
      
      // Add missing axes
      const addMissingAxes = async () => {
        currentAxisIds.forEach(axisId => {
          if (!existingAxisIds.has(axisId)) {
            console.log(`🔧 Adding missing Y-axis: ${axisId}`)
            const explicitConfig = chart.yAxes?.[axisId as keyof typeof chart.yAxes]
            
            // Use consolidated axis creation function
            const yAxis = createYAxisWithConfig(axisId, explicitConfig, chart.curves, wasmContextRef.current)
            
            chartRef.current.yAxes.add(yAxis)
            yAxisMap.set(axisId, yAxis)
          }
        })
      }
      
      await addMissingAxes()
      
      // Rebuild yAxisMap after changes
      yAxisMap.clear()
      chartRef.current.yAxes.asArray().forEach((axis: any) => {
        // Determine axis ID from the axis itself
        let axisId = axis.id || 'left'
        yAxisMap.set(axisId, axis)
      })
      
      // Clear existing series
      chartRef.current.renderableSeries.clear()
      
      // Add updated curves
      chart.curves.forEach((curve, index) => {
        console.log(`🔍 Curve ${index + 1}/${chart.curves.length}: "${curve.name}"`, {
          hasXValues: !!curve.xValues,
          hasYValues: !!curve.yValues,
          xLength: curve.xValues?.length || 0,
          yLength: curve.yValues?.length || 0,
          color: curve.color || 'no color',
          yAxisId: curve.yAxisId || 'no yAxisId',
          visible: curve.visible
        })
        
        // Log time span if we have xValues
        if (curve.xValues && curve.xValues.length > 0) {
          const firstTime = curve.xValues[0]
          const lastTime = curve.xValues[curve.xValues.length - 1]
          console.log(`  ⏰ Time span: ${firstTime} to ${lastTime}`)
        }

        try {
          const dataSeries = new XyDataSeries(wasmContextRef.current, {
            dataSeriesName: curve.displayName ?? curve.name,
          })

          // Add data points - all x values are Date objects, convert to milliseconds
          let pointsAdded = 0
          if (curve.xValues && curve.yValues) {
            curve.xValues.forEach((x, i) => {
              const xValue = x instanceof Date ? x.getTime() : Number(x)
              if (curve.yValues[i] !== undefined && curve.yValues[i] !== null) {
                dataSeries.append(xValue, curve.yValues[i])
                pointsAdded++
              }
            })
            console.log(`  ✅ Added ${pointsAdded} data points to "${curve.name}"`)
          } else {
            console.log(`  ❌ No data to plot for "${curve.name}" - missing xValues or yValues`)
          }

          // Determine which Y-axis to use
          const yAxisId = curve.yAxisId || 'left'
          const targetYAxis = yAxisMap.get(yAxisId)
          
          // Y-axis should always exist because we created them based on curve references
          if (!targetYAxis) {
            throw new Error(`Y-axis '${yAxisId}' not found for curve '${curve.name}'`)
          }

          // Use ChartSeriesFactory to properly apply all styling
          const lineSeries = ChartSeriesFactory.createSeriesFromCurve(
            wasmContextRef.current,
            dataSeries,
            targetYAxis.id,
            curve
          )
          // Respect persistent visibility from template, but do NOT use it for legend toggle
          ;(lineSeries as any).isVisible = curve.visible !== false
          // Apply opacity trick for local hide (keeps series in autorange, avoids re-zoom)
          const curveId = getCurveIdFromCurve(curve)
          const isHidden = curveId ? hiddenCurveIds.has(curveId) : false
          ;(lineSeries as any).opacity = isHidden ? 0 : (curve.opacity ?? 1)

          chartRef.current.renderableSeries.add(lineSeries)
          console.log(`  🎯 Successfully added "${curve.name}" to chart (total series: ${chartRef.current.renderableSeries.size()})`)
        } catch (error) {
          console.warn(`❌ Failed to create series for curve ${curve.name}:`, error)
        }
      })
      
      console.log(`📋 Chart update summary for ${chartId}:`, {
        totalCurvesReceived: chart.curves.length,
        totalSeriesAdded: chartRef.current.renderableSeries.size(),
        yAxesCount: chartRef.current.yAxes.size()
      })
      
      // Auto-zoom once on initial render only (avoid re-zoom on legend toggles)
      const hasAnyManualRange = Object.values(chart.yAxes || {}).some(axisConfig => !!axisConfig?.range)
      if (!didInitialAutoZoom.current && !hasAnyManualRange && chartRef.current) {
        setTimeout(() => {
          if (chartRef.current) {
            console.log(`🔍 Initial auto-zoom for chart ${chartId}`)
            chartRef.current.zoomExtents()
            didInitialAutoZoom.current = true
          }
        }, 50)
      }
      
      // Force re-render
      chartRef.current.invalidateElement()
      }
    }
    
    updateCurves()
  }, [chart?.curves, chartId, theme, hiddenCurveIds])
  
  // Drag and drop integration
  const dropZone = useChartDropZone({
    chartId,
    onDragOverChange: onDragOverChange // Always enabled
  })
  
  // Check if chart has data
  const hasCurves = chart.curves && chart.curves.length > 0
  const isEmpty = !hasCurves
  

  
  // Get theme colors
  const colors = ChartThemeManager.getChartColors(theme)
  
  // Helper function to get appropriate unit for an axis based on curves assigned to it
  const getCurveUnitForAxis = (curves: any[], axisId: string): string | undefined => {
    const curvesOnAxis = curves.filter(curve => (curve.yAxisId || 'left') === axisId)
    if (curvesOnAxis.length > 0) {
      const firstUnit = curvesOnAxis[0].yUnit
      // If all curves on this axis have the same unit, use it
      const allSameUnit = curvesOnAxis.every(curve => curve.yUnit === firstUnit)
      return allSameUnit ? firstUnit : undefined
    }
    return undefined
  }

  // Consolidated axis creation function to eliminate duplication and ensure consistent IDs
  const createYAxisWithConfig = (
    axisId: string,
    explicitConfig: any,
    curves: any[],
    wasmContext: any
  ) => {
    // Title construction logic (consolidated from all 3 creation sites)
    let axisTitle = 'Y Axis'
    if (explicitConfig?.label && explicitConfig?.unit) {
      axisTitle = `${explicitConfig.label} (${explicitConfig.unit})`
    } else if (explicitConfig?.label) {
      axisTitle = explicitConfig.label
    } else if (explicitConfig?.unit) {
      axisTitle = explicitConfig.unit
    } else {
      // Fallback to curve unit if no explicit config
      axisTitle = getCurveUnitForAxis(curves, axisId) || 'Y Axis'
    }

    const initialAutoRange = explicitConfig?.range ? EAutoRange.Never : EAutoRange.Once
    console.log(`🔍 AXIS_DEBUG: Creating Y-axis ${axisId} with autoRange = ${initialAutoRange}`)
    
    const yAxis = new NumericAxis(wasmContext, {
      id: axisId, // 🎯 Always set explicit ID to prevent detection issues
      axisAlignment: axisId.includes('right') ? EAxisAlignment.Right : EAxisAlignment.Left,
      axisTitle: axisTitle,
      autoRange: initialAutoRange,
      drawMajorGridLines: explicitConfig?.showGridLines ?? true,
      drawMinorGridLines: false,
      axisTitleStyle: { fontSize: 14, fontFamily: 'Arial', color: '#CCCCCC' }
    })

    // Set manual range if configured
    if (explicitConfig?.range) {
      yAxis.visibleRange = new NumberRange(explicitConfig.range.min, explicitConfig.range.max)
      console.log(`🎯 Applied range for axis ${axisId}: ${explicitConfig.range.min} to ${explicitConfig.range.max}`)
    }

    return yAxis
  }

  // Get appropriate tick count based on data time range - let SciChart handle the rest
  const getMaxTicksForTimeRange = (curves: any[]): number => {
    if (!curves || curves.length === 0) {
      return 8 // Default reasonable tick count
    }

    // Find the overall time range across all curves
    let minTime = Infinity
    let maxTime = -Infinity
    
    curves.forEach(curve => {
      if (curve.xValues && curve.xValues.length > 0) {
        curve.xValues.forEach((x: Date | number) => {
          const time = x instanceof Date ? x.getTime() : Number(x)
          minTime = Math.min(minTime, time)
          maxTime = Math.max(maxTime, time)
        })
      }
    })

    if (minTime === Infinity || maxTime === -Infinity) {
      return 8 // No valid data, use default
    }

    // Calculate the time range in hours
    const rangeMs = maxTime - minTime
    const rangeHours = rangeMs / (1000 * 60 * 60)

    // Return appropriate max tick count based on time range
    if (rangeHours > 48) return 8   // ~6 hour intervals  
    if (rangeHours > 24) return 12  // ~2-4 hour intervals
    if (rangeHours > 12) return 8   // ~2-3 hour intervals  
    if (rangeHours > 6) return 6    // ~1-2 hour intervals
    return 8                        // Let SciChart decide for shorter ranges
  }

  // SciChart initialization (based on working TooltipDemo pattern)
  const initChart = async (rootElement: string | HTMLDivElement) => {
    console.log(`Initializing SciChart for ${chartId}`)
    
    const { sciChartSurface, wasmContext } = await SciChartSurface.create(rootElement, {
      theme: ChartThemeManager.createSciChartTheme(theme),
    })

    // Get appropriate tick count based on data range
    const maxAutoTicks = getMaxTicksForTimeRange(chart.curves || [])
    
    // Set up X-axis - always use DateTimeNumericAxis since all curves have Date[] xValues
    console.log(`🔍 AXIS_DEBUG: Creating X-axis with autoRange = ${EAutoRange.Once}`)
    
    // Display timestamps in the user's local timezone by default
    const xAxis = new DateTimeNumericAxis(wasmContext, {
      axisTitle: 'Time',
      autoRange: EAutoRange.Once,
      drawMajorGridLines: true,
      drawMinorGridLines: true, // Enable minor grid lines for better granularity
      drawMinorTickLines: true, // Show minor tick marks
      autoTicks: true, // Let SciChart automatically align ticks to nice boundaries
      maxAutoTicks, // Control maximum number of major ticks based on data range
      axisTitleStyle: { fontSize: 14, fontFamily: 'Arial' },
      labelProvider: new TimeLabelProvider('local')
    })
    sciChartSurface.xAxes.add(xAxis)

    // Set up Y-axes based on chart configuration
    const yAxisMap = new Map<string, any>()
    
    // First, collect all Y-axis IDs referenced by curves
    const referencedAxisIds = new Set<string>()
    if (chart.curves && chart.curves.length > 0) {
      chart.curves.forEach(curve => {
        const yAxisId = curve.yAxisId || 'left' // Default to left
        referencedAxisIds.add(yAxisId)
      })
    }
    
    // Create Y-axes for all referenced IDs, using explicit config if available
    for (const axisId of referencedAxisIds) {
      const explicitConfig = chart.yAxes?.[axisId as keyof typeof chart.yAxes]
      
      // Use consolidated axis creation function
      const yAxis = createYAxisWithConfig(axisId, explicitConfig, chart.curves, wasmContext)
      
      sciChartSurface.yAxes.add(yAxis)
      yAxisMap.set(axisId, yAxis)
    }
    
    // Fallback: ensure at least one Y-axis exists if no curves reference any
    if (referencedAxisIds.size === 0) {
      const yAxis = createYAxisWithConfig('left', undefined, chart.curves, wasmContext)
      sciChartSurface.yAxes.add(yAxis)
      yAxisMap.set('left', yAxis)
    }

    // Add curves as simple line series
    console.log(`📊 ChartContainer received ${chart.curves?.length || 0} curves for chart ${chartId}`)
    
    if (chart.curves && chart.curves.length > 0) {
      chart.curves.forEach((curve, index) => {
        console.log(`🔍 Curve ${index + 1}/${chart.curves.length}: "${curve.name}"`, {
          hasXValues: !!curve.xValues,
          hasYValues: !!curve.yValues,
          xLength: curve.xValues?.length || 0,
          yLength: curve.yValues?.length || 0,
          color: curve.color || 'no color',
          yAxisId: curve.yAxisId || 'no yAxisId',
          visible: curve.visible
        })
        
        // Log time span if we have xValues
        if (curve.xValues && curve.xValues.length > 0) {
          const firstTime = curve.xValues[0]
          const lastTime = curve.xValues[curve.xValues.length - 1]
          console.log(`  ⏰ Time span: ${firstTime} to ${lastTime}`)
        }
        try {
          const dataSeries = new XyDataSeries(wasmContext, {
            dataSeriesName: curve.displayName ?? curve.name,
          })

          // Add data points - all x values are Date objects, convert to milliseconds
          let pointsAdded = 0
          if (curve.xValues && curve.yValues) {
            curve.xValues.forEach((x, i) => {
              const xValue = x instanceof Date ? x.getTime() : Number(x)
              if (curve.yValues[i] !== undefined && curve.yValues[i] !== null) {
                dataSeries.append(xValue, curve.yValues[i])
                pointsAdded++
              }
            })
            console.log(`  ✅ Added ${pointsAdded} data points to "${curve.name}"`)
          } else {
            console.log(`  ❌ No data to plot for "${curve.name}" - missing xValues or yValues`)
          }

          // Determine which Y-axis to use
          const yAxisId = curve.yAxisId || 'left'  // Default to left axis
          const targetYAxis = yAxisMap.get(yAxisId)
          
          // Y-axis should always exist because we created them based on curve references
          if (!targetYAxis) {
            throw new Error(`Y-axis '${yAxisId}' not found for curve '${curve.name}'`)
          }
          
          // Create line series and assign to correct Y-axis
          const lineSeries = new FastLineRenderableSeries(wasmContext, {
            dataSeries,
            stroke: curve.color || colors.text.primary,
            strokeThickness: curve.strokeThickness || 2,
            yAxisId: targetYAxis.id  // Assign to specific Y-axis
          })

          // Respect curve visibility state
          ;(lineSeries as any).isVisible = curve.visible !== false

          sciChartSurface.renderableSeries.add(lineSeries)
          console.log(`  🎯 Successfully added "${curve.name}" to chart (total series: ${sciChartSurface.renderableSeries.size()})`)
        } catch (error) {
          console.warn(`❌ Failed to create series for curve ${curve.name}:`, error)
        }
      })
      
      console.log(`📋 Chart rendering summary for ${chartId}:`, {
        totalCurvesReceived: chart.curves.length,
        totalSeriesAdded: sciChartSurface.renderableSeries.size(),
        yAxesCount: sciChartSurface.yAxes.size()
      })
    }

    // Add zoom and pan modifiers (like TooltipDemo)
    // Conditionally add mouse wheel zoom based on enableMouseWheelZoom prop
    if (enableMouseWheelZoom) {
      sciChartSurface.chartModifiers.add(new MouseWheelZoomModifier())
    }
    sciChartSurface.chartModifiers.add(new ZoomExtentsModifier())
    sciChartSurface.chartModifiers.add(new ZoomPanModifier({ executeOn: EExecuteOn.MouseMiddleButton }))

    // Add initial zoom modifier based on current zoom mode
    switch (currentZoomMode) {
      case 'box':
        sciChartSurface.chartModifiers.add(new RubberBandXyZoomModifier({
          xyDirection: EXyDirection.XyDirection
        }))
        break
      case 'sliceX':
        sciChartSurface.chartModifiers.add(new RubberBandXyZoomModifier({
          xyDirection: EXyDirection.XDirection
        }))
        break
      case 'sliceY':
        sciChartSurface.chartModifiers.add(new RubberBandXyZoomModifier({
          xyDirection: EXyDirection.YDirection
        }))
        break
    }

    // Cursor modifier for crosshairs and tooltips will be added dynamically via useEffect

    // Store reference
    chartRef.current = sciChartSurface
    wasmContextRef.current = wasmContext
    
    // Notify parent
    if (onSurfaceReady) {
      onSurfaceReady(chartId, sciChartSurface)
    }

    console.log(`SciChart initialized for ${chartId}`)
    return { sciChartSurface }
  }

  // Event handlers
  const handleClose = () => {
    if (onClose) {
      onClose(chartId)
    }
  }

  // Title editing removed - titles are no longer editable

  const handleHeightPresetChange = (preset: ChartHeightPreset) => {
    // Notify parent of height change (parent manages the actual state)
    if (onHeightChange) {
      const presetHeights = { small: 250, medium: 400, large: 550, xl: 700 }
      const newHeight = presetHeights[preset]
      onHeightChange(chartId, newHeight)
    }
  }

  const handleZoomModeChange = (mode: ZoomMode) => {
    setCurrentZoomMode(mode)
    // Update chart modifiers when zoom mode changes
    updateZoomModifiers(mode)
  }

  // Function to update zoom modifiers based on current mode
  const updateZoomModifiers = (mode: ZoomMode) => {
    if (!chartRef.current) return

    // Remove existing rubber band zoom modifiers
    const surface = chartRef.current
    
    console.log(`🔍 Updating zoom modifiers to mode: ${mode}`)
    
    // Clear existing rubber band zoom modifiers by filtering them out
    const modifiersToRemove: any[] = []
    for (let i = 0; i < surface.chartModifiers.size(); i++) {
      const modifier = surface.chartModifiers.get(i)
      if (modifier instanceof RubberBandXyZoomModifier) {
        modifiersToRemove.push(modifier)
      }
    }
    
    console.log(`🗑️ Removing ${modifiersToRemove.length} existing RubberBandXyZoomModifier(s)`)
    
    // Remove the found modifiers
    modifiersToRemove.forEach(modifier => {
      surface.chartModifiers.remove(modifier)
    })

    // Add simple zoom modifier - let SciChart handle autoRange automatically
    switch (mode) {
      case 'box':
        surface.chartModifiers.add(new RubberBandXyZoomModifier({
          xyDirection: EXyDirection.XyDirection
        }))
        break
      case 'sliceX':
        surface.chartModifiers.add(new RubberBandXyZoomModifier({
          xyDirection: EXyDirection.XDirection
        }))
        break
      case 'sliceY':
        surface.chartModifiers.add(new RubberBandXyZoomModifier({
          xyDirection: EXyDirection.YDirection
        }))
        break
    }
    console.log(`✅ Added new ${mode} zoom modifier with autoRange handling`)
  }

  // Function to update cursor modifiers based on crosshairs and tooltips state
  const updateCursorModifiers = () => {
    if (!chartRef.current) return

    const surface = chartRef.current
    
    // Remove existing cursor modifiers
    const modifiersToRemove: any[] = []
    for (let i = 0; i < surface.chartModifiers.size(); i++) {
      const modifier = surface.chartModifiers.get(i)
      if (modifier instanceof CursorModifier) {
        modifiersToRemove.push(modifier)
      }
    }
    
    modifiersToRemove.forEach(modifier => {
      surface.chartModifiers.remove(modifier)
    })

    // Add cursor modifier if crosshairs or tooltips are enabled
    if (crosshairsEnabled || tooltipsEnabled) {
      const cursorModifier = new CursorModifier({
        showTooltip: tooltipsEnabled,
        showAxisLabels: tooltipsEnabled,
        showXLine: crosshairsEnabled,
        showYLine: crosshairsEnabled,
        hitTestRadius: 10,
        axisLabelFill: theme === 'dark' ? '#333' : '#f0f0f0',
        axisLabelStroke: theme === 'dark' ? '#fff' : '#000',
        tooltipDataTemplate: (seriesInfos) => {
          const lineItems: string[] = []
          seriesInfos.forEach((si) => {
            if (si.isHit) {
              lineItems.push(`${si.seriesName}: ${si.yValue.toFixed(2)}`)
              // All axes are datetime - format timestamps as simple numeric date-time
              const date = new Date(si.xValue)
              const dateTimeString = date.toISOString().slice(0, 19).replace('T', ' ') + '.' + Math.floor(date.getMilliseconds() / 100)
              lineItems.push(`Time: ${dateTimeString}`)
            }
          })
          return lineItems
        }
      })
      surface.chartModifiers.add(cursorModifier)
    }
  }

  const handleResetZoom = () => {
    if (chartRef.current) {
      chartRef.current.zoomExtents()
    }
  }

  // Container styles
  const containerStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    height: actualHeight,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    backgroundColor: colors.background,
    overflow: 'hidden',
    ...style
  }

  const headerStyles: React.CSSProperties = {
    display: 'flex',
    padding: '8px 16px',
    backgroundColor: colors.background,
    borderBottom: `1px solid ${colors.border}`,
    minHeight: '40px'
  }

  return (
    <Box
      ref={dropZone.setNodeRef}
      className={className}
      sx={containerStyles}
      data-testid={`chart-container-${chartId}`}
    >
      {/* Chart Header */}
      <Box sx={headerStyles} data-testid={`chart-header-${chartId}`}>
        {/* Single row: Title and all controls on the right */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 2 }}>
          {/* Left side: Title + compact legend */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flex: 1 }}>
            <ChartTitle
              chartId={chartId}
              title={chart.title || 'Untitled Chart'}
              editable={false} // Always false now
              theme={theme}
              placeholder="Enter chart title..."
              maxLength={100}
            />
            {/* No integrated legend to preserve header space */}
          </Box>
          
          {/* All controls on the right side */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* View Controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ fontSize: '12px', color: colors.text.secondary }}>View:</Box>
              <CrosshairsControl
                enabled={crosshairsEnabled}
                onToggle={setCrosshairsEnabled}
                theme={theme}
              />
              <TooltipsControl
                enabled={tooltipsEnabled}
                onToggle={setTooltipsEnabled}
                theme={theme}
              />
            </Box>
            
            {/* Height Controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ fontSize: '12px', color: colors.text.secondary }}>Height:</Box>
              <ChartHeightPicker
                value={currentHeightPreset}
                onChange={handleHeightPresetChange}
                theme={theme}
              />
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ fontSize: '12px', color: colors.text.secondary }}>Zoom:</Box>
              <ZoomControls
                mode={currentZoomMode}
                onModeChange={handleZoomModeChange}
                chartSurfaces={chartRef.current ? [chartRef.current] : []}
                onResetZoom={handleResetZoom}
                theme={theme}
              />
            </Box>
            {/* Legend Corner Control - compact dropdown with custom icon */}
            <MuiTooltip title="Legend position">
              <IconButton size="small" onClick={openLegendMenu} aria-label="Legend position"
                sx={{
                  width: '32px',
                  height: '32px',
                  border: '1px solid',
                  borderColor: colors.border,
                  borderRadius: '4px',
                  padding: 0,
                  backgroundColor: 'transparent',
                  color: colors.text.secondary,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.04)'
                  }
                }}
              >
                <CornerIcon corner={currentLegendCorner} />
              </IconButton>
            </MuiTooltip>

            <Menu anchorEl={legendMenuAnchor} open={!!legendMenuAnchor} onClose={closeLegendMenu} keepMounted>
              <MenuItem onClick={() => { selectLegendCorner('tl') }}> <CornerIcon corner="tl" /> </MenuItem>
              <MenuItem onClick={() => { selectLegendCorner('tr') }}> <CornerIcon corner="tr" /> </MenuItem>
              <MenuItem onClick={() => { selectLegendCorner('bl') }}> <CornerIcon corner="bl" /> </MenuItem>
              <MenuItem onClick={() => { selectLegendCorner('br') }}> <CornerIcon corner="br" /> </MenuItem>
              <MenuItem onClick={() => { setCurrentLegendMode('bottom'); onLegendModeChange?.(chartId, 'bottom'); closeLegendMenu() }}> <LegendBottomIcon width={18} height={18} /> </MenuItem>
              <MenuItem onClick={() => { setCurrentLegendMode('none'); onLegendModeChange?.(chartId, 'none'); closeLegendMenu() }}> <LegendNoneIcon width={18} height={18} /> </MenuItem>
            </Menu>

            <ChartCloseButton
              chartId={chartId}
                onClose={handleClose}
                theme={theme}
                requireConfirmation={false}
                aria-label={`Close ${chart.title || 'chart'}`}
              />
          </Box>
        </Box>
      </Box>
      
      {/* Chart Content */}
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: colors.background
        }}
        data-testid={`chart-content-${chartId}`}
      >
        {isEmpty ? (
          // Empty state
          <EmptyChartPlaceholder
            theme={theme}
            title="No Data"
            description="Drop curves here to add them to this chart"
            showDragHint={true}
          />
        ) : (
          // SciChart rendering
          <SciChartReact
            initChart={initChart}
            style={{
              width: '100%',
              height: '100%'
            }}
          />
        )}

        {/* Overlay legend when requested */}
        {hasCurves && currentLegendMode === 'overlay' && (
          <LegendCompact 
            items={legendItems} 
            theme={theme} 
            corner={currentLegendCorner}
            onItemClick={item => {
              if (item.curveId) {
                onLegendItemClick?.(chartId, item.curveId)
              }
            }}
            onToggleVisibility={item => {
              handleLegendToggleLocal(item)
            }}
          />
        )}
      
        {/* Drag Overlay */}
        <DragOverlay
          isVisible={dropZone.isOver || isDraggedOver}
          theme={theme}
          text="Drop curve here"
          showPulse={true}
        />
      </Box>

      {/* Bottom legend strip when requested */}
      {hasCurves && currentLegendMode === 'bottom' && (
        <LegendStrip 
          items={legendItems}
          theme={theme}
          onItemClick={item => { if (item.curveId) onLegendItemClick?.(chartId, item.curveId) }}
          onToggleVisibility={item => { handleLegendToggleLocal(item) }}
        />
      )}
    </Box>
  )
}

export default ChartContainer 
